/**
 * React Query hook for fetching organization data
 *
 * This hook replaces manual sessionStorage caching in OrganizationContext
 * with React Query's automatic cache management.
 *
 * Benefits:
 * - Automatic cache invalidation across all components
 * - Request deduplication (prevents duplicate API calls)
 * - Built-in error handling and retry logic
 * - Loading states out of the box
 * - No race conditions or stale data bugs
 *
 * Usage:
 * ```typescript
 * const { data: organization, isLoading, error } = useOrganizationQuery(selectedOrganizationId);
 * ```
 */

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from './query-keys';
import { fetchWithBypass } from '@/lib/platform/utils/fetch';
import { logger } from '@/lib/platform/utils/logger';
import { Organization } from '@/lib/platform/types';
import { useSession } from '@/lib/platform/auth/client';

/**
 * Fetches organization data from API
 *
 * @param selectedOrganizationId - Optional organization ID to fetch. If not provided, fetches default organization
 * @returns Organization data or null if user has no organization
 */
async function fetchOrganization(
  selectedOrganizationId?: string | null
): Promise<Organization | null> {
  const url = selectedOrganizationId
    ? `/api/organizations?selectedOrganizationId=${selectedOrganizationId}`
    : '/api/organizations';

  logger.log('[useOrganizationQuery] Fetching from:', url);

  const response = await fetchWithBypass(url);
  const data = await response.json();

  logger.log('[useOrganizationQuery] API response:', data);

  // Handle both single organization and list response
  const organizationData = data.organization || (data.organizations && data.organizations[0]);

  if (response.ok && organizationData) {
    return organizationData;
  } else if (
    response.ok &&
    (!organizationData || (data.organizations && data.organizations.length === 0))
  ) {
    // FIX: If we're looking for a specific organization ID but it doesn't exist yet,
    // throw an error so React Query will retry (handles Stripe webhook delay during onboarding)
    if (selectedOrganizationId) {
      logger.log(
        '[useOrganizationQuery] Organization not found yet, will retry:',
        selectedOrganizationId
      );
      throw new Error('Organization not found yet - retrying...');
    }
    // User has no organization (fresh account) - this is fine, don't retry
    logger.log('[useOrganizationQuery] No organization found for user (onboarding)');
    return null;
  } else if (response.status === 401) {
    // Unauthorized - user is logged out, this is expected
    logger.log('[useOrganizationQuery] User not authenticated (logged out)');
    return null;
  } else if (response.status === 403) {
    // Access denied to the selected organization — the stored ID is stale
    // (e.g. after impersonation, or user was removed from the org).
    // Fall back to the user's own organization list instead of returning null,
    // which made the app think "no organization" → endless /platform/welcome spinner.
    logger.warn(
      '[useOrganizationQuery] Access denied to selected organization, falling back to own org list'
    );
    const fallbackResponse = await fetchWithBypass('/api/organizations');
    const fallbackData = await fallbackResponse.json();
    const fallbackOrg =
      fallbackData.organization || (fallbackData.organizations && fallbackData.organizations[0]);
    if (fallbackResponse.ok && fallbackOrg) {
      return fallbackOrg;
    }
    if (selectedOrganizationId) {
      // No own org yet — likely onboarding where the Stripe webhook hasn't
      // created the org/member rows. Throw so React Query retries (same
      // behavior as the "organization not found yet" case above).
      throw new Error('Organization not found yet - retrying...');
    }
    return null;
  } else if (response.status === 410) {
    // Organization is deleted (soft-deleted)
    logger.log('[useOrganizationQuery] Organization is deleted (410 Gone)');
    return null;
  } else {
    // Actual error from API
    logger.error('[useOrganizationQuery] Failed to load organization:', data.error);
    throw new Error(data.error || 'Failed to load organization');
  }
}

/**
 * React Query hook for fetching organization data
 *
 * @param selectedOrganizationId - Optional organization ID to fetch
 * @returns React Query result with organization data, loading state, and error
 */
export function useOrganizationQuery(selectedOrganizationId?: string | null) {
  const { data: session, isPending: isSessionPending } = useSession();

  const result = useQuery({
    queryKey: queryKeys.organization.detail(selectedOrganizationId),
    queryFn: () => fetchOrganization(selectedOrganizationId),
    // Only fetch when session is ready and user is authenticated
    enabled: !isSessionPending && !!session?.user,
    // Cache for 5 minutes (organization data doesn't change frequently)
    staleTime: 5 * 60 * 1000,
    // Keep in cache for 15 minutes after last use
    gcTime: 15 * 60 * 1000,
    // FIX: Retry more aggressively when we have a selectedOrganizationId
    // This handles the case where user completes payment and organization
    // is being created by Stripe webhook (may take a few seconds)
    retry: selectedOrganizationId ? 10 : 1, // Retry 10 times if we're looking for a specific org
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000), // Exponential backoff: 1s, 2s, 4s, 5s, 5s...
    // Don't refetch on window focus (organization data is stable)
    refetchOnWindowFocus: false,
  });

  // Return with isFetching explicitly exposed for organization context
  return result;
}
